import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';

const CardContainer = styled.div`
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.md};
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  
  ${props => props.hoverable && `
    &:hover {
      box-shadow: ${props.theme.shadows.lg};
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.clickable && `
    cursor: pointer;
    
    &:hover {
      box-shadow: ${props.theme.shadows.lg};
    }
    
    &:active {
      transform: translateY(1px);
    }
  `}
`;

const CardHeader = styled.div`
  padding: 1.5rem 1.5rem 0 1.5rem;
  border-bottom: ${props => props.divider ? `1px solid ${props.theme.colors.border.light}` : 'none'};
  margin-bottom: ${props => props.divider ? '1rem' : '0'};
`;

const CardTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0.5rem 0 0 0;
`;

const CardBody = styled.div`
  padding: ${props => props.noPadding ? '0' : '1.5rem'};
`;

const CardFooter = styled.div`
  padding: 0 1.5rem 1.5rem 1.5rem;
  border-top: ${props => props.divider ? `1px solid ${props.theme.colors.border.light}` : 'none'};
  margin-top: ${props => props.divider ? '1rem' : '0'};
  background-color: ${props => props.theme.colors.background.secondary};
`;

const CardComponent = ({
  children,
  title,
  subtitle,
  hoverable = false,
  clickable = false,
  onClick,
  className,
  ...props
}) => {
  const { theme } = useTheme();
  
  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };
  
  return (
    <CardContainer
      theme={theme}
      hoverable={hoverable}
      clickable={clickable}
      onClick={handleClick}
      className={className}
      {...props}
    >
      {(title || subtitle) && (
        <CardHeader theme={theme} divider={!!children}>
          {title && <CardTitle theme={theme}>{title}</CardTitle>}
          {subtitle && <CardSubtitle theme={theme}>{subtitle}</CardSubtitle>}
        </CardHeader>
      )}
      {children && (
        <CardBody theme={theme}>
          {children}
        </CardBody>
      )}
    </CardContainer>
  );
};

// Export sub-components for flexible usage
CardComponent.Header = CardHeader;
CardComponent.Title = CardTitle;
CardComponent.Subtitle = CardSubtitle;
CardComponent.Body = CardBody;
CardComponent.Footer = CardFooter;

export default CardComponent;

